import { isAxiosError } from 'axios'
import { showErrorToast } from 'components/utils/util'
import { isForbiddenRequestError } from 'lib/error'

export const DEFAULT_DISPLAY_MESSAGE =
  'Something went wrong. ' +
  'Please check your internet connection. ' +
  'If you require assistance, please contact us.'

const apiPathsRegexToIgnore = [
  /\/admin\/company\/\d+\/cancellation_policy/,
  /\/admin\/company\/\d+\/name/,
  /\/admin\/company\/\d+\/min_pay_policy/,
  /\/admin\/company\/\d+\/logo/,
  /\/admin\/company\/\d+/,
  /\/admin\/shift\/\d+\/work\/\d+/,
  /\/admin\/company_group\/list/,
  /\/admin\/company\/list/,
  /\/admin\/company_group/,
  /\/admin\/shift\/all/,
  /\/admin\/shift\/\d+\/tier_stats/,
  /\/admin\/shifts\/tier_stats/,
  /\/admin\/vms\/timesheet_upload\/\d+/,
]

/**
 * UserRequestError / handled Werkzeug errors
 * Standard Bad Request envelope for user-resolvable failures.
 *
 * @example
 * {
 *   message: {
 *     general: ["Example user request error message"]
 *   },
 *   error_type?: "needs_phone_verification"
 * }
 */
type UserRequestError = {
  message: {
    general: string[]
  }
  error_type?: string
}

/**
 * marshmallow.ValidationError
 * Legacy field-map validation error.
 *
 * @example
 * {
 *   message: {
 *     fieldName: ["Error message"],
 *     fieldName2: ["Error message 2"]
 *   }
 * }
 */
type MarshmallowMessageMap = {
  [field: string]: string[] | MarshmallowMessageMap | string
}

type MarshmallowValidationError = {
  message: MarshmallowMessageMap
}

/**
 * Pydantic RequestValidationError
 * Current request body / args validation error.
 *
 * @example
 * {
 *   errors: [
 *     { loc: ["body", "name"], msg: "Name is required.", type: "value_error" },
 *     { loc: ["body", "age"], msg: "Age must be a positive integer.", type: "value_error" }
 *   ]
 * }
 */
type PydanticValidationError = {
  errors: {
    loc: (string | number)[]
    msg: string
    type: string
    input?: unknown
    ctx?: Record<string, unknown>
  }[]
}

const isUserRequestError = (data: unknown): data is UserRequestError =>
  Array.isArray((data as UserRequestError)?.message?.general)

const isMarshmallowValidationError = (
  data: unknown
): data is MarshmallowValidationError => {
  const message = (data as MarshmallowValidationError)?.message
  return (
    typeof message === 'object' &&
    message !== null &&
    !Array.isArray(message.general)
  )
}

const isPydanticValidationError = (
  data: unknown
): data is PydanticValidationError =>
  Array.isArray((data as PydanticValidationError)?.errors)

/** Format a known API error body into a user-facing string. */
function getMessageString(data: unknown, responseStatus?: number): string {
  if (typeof data === 'string') {
    return data
  }

  if (typeof data === 'object' && data !== null) {
    if (isForbiddenRequestError(data, responseStatus)) {
      return data.message
    }

    if (isUserRequestError(data)) {
      return data.message.general[0]
    }

    if (isMarshmallowValidationError(data)) {
      return Object.values(data.message).flat().join('. ')
    }

    if (isPydanticValidationError(data)) {
      return data.errors.map((e) => e.msg).join('. ')
    }
  }

  // Unknown shapes are usually not user-friendly (e.g. raw 500 bodies)
  return ''
}

/** Get the error message string from an Axios error or related failure. */
export const parseErrorMessage = (error: unknown): string => {
  if (error === undefined || error === null) {
    return ''
  }

  if (typeof error === 'string') {
    return error
  }

  if (isAxiosError(error)) {
    return (
      getMessageString(error.response?.data, error.response?.status) ||
      DEFAULT_DISPLAY_MESSAGE
    )
  }

  if (error instanceof Error) {
    return error.message || DEFAULT_DISPLAY_MESSAGE
  }

  return DEFAULT_DISPLAY_MESSAGE
}

/** Show toast for any API error, then re-reject so callers still see the failure. */
export const onErrorResponse = (error: unknown) => {
  if (!error) {
    return Promise.reject(error)
  }

  if (
    isAxiosError(error) &&
    apiPathsRegexToIgnore.some((regex) => {
      const url = error.config?.url
      return url !== undefined && regex.test(url)
    })
  ) {
    return Promise.reject(error)
  }

  const message = parseErrorMessage(error)
  showErrorToast(
    typeof message === 'string' && message.length > 0
      ? message
      : DEFAULT_DISPLAY_MESSAGE
  )
  return Promise.reject(error)
}
